<section class="home-page" [@pageEnter] (mousemove)="onHeroPointerMove($event)" [ngStyle]="getHeroCursorStyle()">
  <section class="hero-section" aria-labelledby="hero-title">
    <video #heroVideoElement class="hero-video" autoplay muted="muted" [muted]="true" loop playsinline preload="metadata" [poster]="heroPoster" aria-hidden="true" (loadedmetadata)="enforceHeroVideoSilence()">
      <source [src]="heroVideo" type="video/mp4" />
    </video>
    <div class="hero-overlay" aria-hidden="true"></div>
    <div class="hero-orb hero-orb-one" aria-hidden="true"></div>
    <div class="hero-orb hero-orb-two" aria-hidden="true"></div>

    <div class="launch-signal-strip motion-reveal" role="status" aria-label="Live launch signal status">
      <span class="launch-signal-core" aria-hidden="true">
        <i></i><i></i><i></i>
      </span>
      <span class="launch-signal-copy">Live launch signal</span>
      <strong>96%</strong>
      <span class="launch-signal-spark" aria-hidden="true"></span>
    </div>

    <div class="hero-content motion-reveal">
      <p class="eyebrow">Carmotive CRM · EV Retail · Luxury Workflows</p>
      <h1 id="hero-title">Discover The Future Of Driving</h1>
      <p class="hero-lead">Manage premium vehicle leads, EV inventory and luxury sales journeys</p>
      <div class="hero-actions" aria-label="Homepage primary actions">
        <a class="premium-button premium-button-light" routerLink="/table-list">Explore Cars</a>
        <a class="premium-button premium-button-ghost" routerLink="/about">Compare Models</a>
      </div>

      <div class="hero-proof" aria-label="Carmotive CRM experience highlights">
        <div>
          <strong>18K+</strong>
          <span>qualified launch leads</span>
        </div>
        <div>
          <strong>4.9/5</strong>
          <span>shopper experience score</span>
        </div>
        <div>
          <strong>24/7</strong>
          <span>EV insight monitoring</span>
        </div>
      </div>
    </div>

    <aside class="hero-dashboard motion-reveal" aria-label="Featured premium car insight">
      <span class="live-pill"><i aria-hidden="true"></i> Live launch signal</span>
      <h2>Porsche Taycan Turbo GT</h2>
      <p>High-intent EV buyers are comparing range, charging confidence, and premium performance this week.</p>
      <div class="signal-wave" aria-hidden="true"><span></span><span></span><span></span></div>
      <div class="dashboard-meter" aria-hidden="true"><span></span></div>
      <dl>
        <div><dt>Intent</dt><dd>96%</dd></div>
        <div><dt>SLA</dt><dd>4m</dd></div>
      </dl>
    </aside>

    <div class="hero-meta motion-reveal" aria-label="Luxury automotive highlights">
      <span>Electric range insights</span>
      <span>Launch timelines</span>
      <span>Premium comparisons</span>
    </div>

    <a class="scroll-indicator" href="#showcase" aria-label="Scroll to 3D car showcase">
      <span></span>
      Scroll
    </a>
  </section>

  <section #showcase id="showcase" class="showcase-section" aria-labelledby="showcase-title" (mousemove)="onShowcasePointerMove($event)" (mouseleave)="resetShowcaseTilt()" [ngStyle]="getShowcaseStyle()">
    <div class="section-heading motion-reveal">
      <p class="eyebrow">Interactive 3D showcase</p>
      <h2 id="showcase-title">A sculpted grand tourer that reacts to every move.</h2>
      <p>
        Mouse movement changes the vehicle attitude while scroll progress adds camera-style zoom,
        rotation, and studio lighting motion for a React Three Fiber inspired experience.
      </p>
    </div>

    <div class="studio-stage" aria-label="Silent interactive rotating 3D vehicle illustration">
      <div class="studio-grid" aria-hidden="true"></div>
      <div class="light-beam light-beam-left" aria-hidden="true"></div>
      <div class="light-beam light-beam-right" aria-hidden="true"></div>
      <div class="vehicle-3d" aria-hidden="true" data-audio="silent">
        <div class="vehicle-roof"></div>
        <div class="vehicle-body">
          <span class="vehicle-window vehicle-window-front"></span>
          <span class="vehicle-window vehicle-window-rear"></span>
          <span class="vehicle-headlight"></span>
          <span class="vehicle-taillight"></span>
        </div>
        <span class="wheel wheel-front"></span>
        <span class="wheel wheel-rear"></span>
        <div class="vehicle-reflection"></div>
      </div>
      <div class="showcase-specs glass-panel">
        <span>Camera zoom</span>
        <strong>{{ (scrollProgress * 100) | number:'1.0-0' }}%</strong>
      </div>
      <div class="showcase-chapters" aria-hidden="true">
        <span [class.active]="scrollProgress < 0.34">Studio reveal</span>
        <span [class.active]="scrollProgress >= 0.34 && scrollProgress < 0.68">Performance detail</span>
        <span [class.active]="scrollProgress >= 0.68">Shortlist action</span>
      </div>
    </div>
  </section>

  <section class="suggestion-section" aria-labelledby="suggestion-title">
    <div class="suggestion-card glass-panel motion-reveal">
      <div>
        <p class="eyebrow">Design suggestions applied</p>
        <h2 id="suggestion-title">A more attractive journey with clearer action, trust, and premium depth.</h2>
      </div>
      <ul class="suggestion-list" aria-label="UI and UX enhancements">
        <li><span>01</span><strong>Hero trust metrics</strong><small>Instant credibility above the fold.</small></li>
        <li><span>02</span><strong>Live insight card</strong><small>Creates a dynamic CRM dashboard feel.</small></li>
        <li><span>03</span><strong>Premium surfaces</strong><small>Layered glass, glow, and hover feedback.</small></li>
      </ul>
    </div>
  </section>

  <section class="featured-section" aria-labelledby="featured-title">
    <div class="section-heading motion-reveal">
      <p class="eyebrow">Upcoming highlights</p>
      <h2 id="featured-title">Premium launches ready for your shortlist.</h2>
      <p>
        Compare standout upcoming models with launch timing, pricing, and brand positioning at a glance.
      </p>
    </div>

    <div class="featured-grid">
      <article class="featured-card" *ngFor="let car of featuredCars">
        <img [src]="car.image" [alt]="car.brand + ' ' + car.name" loading="lazy" />
        <div class="featured-overlay" aria-hidden="true"></div>
        <div class="featured-content">
          <p>{{ car.brand }}</p>
          <h3>{{ car.name }}</h3>
          <dl>
            <div>
              <dt>Launch</dt>
              <dd>{{ car.launchDate }}</dd>
            </div>
            <div>
              <dt>From</dt>
              <dd>{{ car.price }}</dd>
            </div>
          </dl>
        </div>
      </article>
    </div>
  </section>


  <section class="concierge-section" aria-labelledby="concierge-title">
    <div class="concierge-shell glass-panel motion-reveal">
      <div class="concierge-copy">
        <p class="eyebrow">MagicPath-inspired UX</p>
        <h2 id="concierge-title">Build a launch-ready shortlist in seconds.</h2>
        <p>Choose the ownership priority that matters most and instantly see matching premium recommendations, confidence signals, and next actions.</p>
      </div>

      <div class="concierge-panel" aria-label="Interactive car shortlist concierge">
        <div
          class="launch-orbit"
          [class.orbit-range]="activeConcierge.themeClass === 'range'"
          [class.orbit-family]="activeConcierge.themeClass === 'family'"
          [class.orbit-adventure]="activeConcierge.themeClass === 'adventure'"
          [ngStyle]="getConciergeOrbitStyle()"
          (mousemove)="onConciergePointerMove($event)"
          (mouseleave)="resetConciergePointer()"
          aria-hidden="true">
          <div class="orbit-road"></div>
          <div class="orbit-ring orbit-ring-one"></div>
          <div class="orbit-ring orbit-ring-two"></div>
          <div class="charging-arc charging-arc-one"></div>
          <div class="charging-arc charging-arc-two"></div>
          <div class="orbit-terrain"></div>
          <div class="orbit-car" [class]="'orbit-car orbit-car-' + activeConcierge.themeClass">
            <span class="orbit-cabin"></span>
            <span class="orbit-light orbit-light-front"></span>
            <span class="orbit-light orbit-light-rear"></span>
            <span class="orbit-wheel orbit-wheel-front"></span>
            <span class="orbit-wheel orbit-wheel-rear"></span>
          </div>
          <div class="orbit-label">{{ activeConcierge.model }}</div>
        </div>

        <div class="choice-grid" role="list" aria-label="Shortlist priorities">
          <button
            type="button"
            class="choice-card"
            *ngFor="let option of conciergeOptions"
            [class.active]="option.key === activeConciergeKey"
            (click)="setConcierge(option.key)"
            [attr.aria-pressed]="option.key === activeConciergeKey">
            <span>{{ option.icon }}</span>
            <strong>{{ option.label }}</strong>
            <small>{{ option.hint }}</small>
          </button>
        </div>

        <article class="recommendation-card">
          <div>
            <span class="match-pill">{{ activeConcierge.match }} match</span>
            <h3>{{ activeConcierge.model }}</h3>
            <p>{{ activeConcierge.reason }}</p>
          </div>
          <dl class="recommendation-metrics">
            <div *ngFor="let metric of activeConcierge.metrics">
              <dt>{{ metric.label }}</dt>
              <dd>{{ metric.value }}</dd>
            </div>
          </dl>
          <button type="button" class="premium-button premium-button-light recommendation-action" (click)="openShortlist()">{{ activeConcierge.cta }}</button>
        </article>
      </div>
    </div>
  </section>

  <div class="shortlist-drawer" *ngIf="shortlistOpen" role="dialog" aria-modal="true" aria-labelledby="shortlist-drawer-title">
    <button type="button" class="shortlist-backdrop" aria-label="Close shortlist preview" (click)="closeShortlist()"></button>
    <aside class="shortlist-panel glass-panel">
      <button type="button" class="drawer-close" aria-label="Close shortlist preview" (click)="closeShortlist()">×</button>
      <p class="eyebrow">3D shortlist stack</p>
      <h2 id="shortlist-drawer-title">{{ activeConcierge.model }} is ready for comparison.</h2>
      <div class="shortlist-stack" aria-hidden="true">
        <article class="stack-card stack-card-back">{{ conciergeOptions[2].model }}</article>
        <article class="stack-card stack-card-mid">{{ conciergeOptions[1].model }}</article>
        <article class="stack-card stack-card-front">{{ activeConcierge.model }}</article>
      </div>
      <p>{{ activeConcierge.reason }}</p>
      <div class="drawer-actions">
        <a class="premium-button premium-button-light" routerLink="/table-list" (click)="closeShortlist()">View shortlist</a>
        <a class="premium-button premium-button-ghost" routerLink="/about" (click)="closeShortlist()">Compare models</a>
      </div>
    </aside>
  </div>

  <section class="why-section" aria-labelledby="why-title">
    <div class="section-heading motion-reveal">
      <p class="eyebrow">Why choose us</p>
      <h2 id="why-title">A calmer, sharper way to research your next car.</h2>
    </div>

    <div class="feature-grid">
      <article class="feature-card glass-panel" *ngFor="let feature of featureCards">
        <span>{{ feature.icon }}</span>
        <h3>{{ feature.title }}</h3>
        <p>{{ feature.description }}</p>
      </article>
    </div>
  </section>

  <section #statsSection class="stats-section" aria-label="Carmotive CRM platform statistics">
    <article class="stat-card glass-panel" *ngFor="let stat of stats">
      <strong>{{ stat.current | number }}{{ stat.suffix }}</strong>
      <span>{{ stat.label }}</span>
    </article>
  </section>

  <section #storySection class="story-section" aria-labelledby="story-title">
    <div class="story-sticky">
      <div class="story-copy motion-reveal">
        <p class="eyebrow">Scroll storytelling</p>
        <h2 id="story-title">From executive comfort to electric futures.</h2>
        <p>
          A pinned, Porsche-style sequence transitions between body styles as you scroll,
          creating a cinematic buying journey from sedan to SUV, EV, and concept car.
        </p>
      </div>

      <div class="story-visual">
        <article class="story-slide" *ngFor="let step of storySteps; let i = index" [class.active]="i === activeStoryIndex">
          <img [src]="step.image" [alt]="step.title" loading="lazy" />
          <div class="story-slide-content">
            <span>{{ step.label }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </article>
      </div>
    </div>
  </section>

  <section class="testimonial-section" aria-labelledby="testimonial-title">
    <div class="section-heading motion-reveal">
      <p class="eyebrow">Drivers and enthusiasts</p>
      <h2 id="testimonial-title">Loved by people who obsess over what comes next.</h2>
    </div>

    <div class="testimonial-slider glass-panel">
      <article class="testimonial-slide" *ngFor="let testimonial of testimonials; let i = index" [class.active]="i === activeTestimonialIndex">
        <p>“{{ testimonial.quote }}”</p>
        <div>
          <strong>{{ testimonial.name }}</strong>
          <span>{{ testimonial.role }}</span>
        </div>
      </article>
      <div class="testimonial-dots" aria-label="Choose testimonial">
        <button type="button" *ngFor="let testimonial of testimonials; let i = index" [class.active]="i === activeTestimonialIndex" (click)="setTestimonial(i)" [attr.aria-label]="'Show testimonial ' + (i + 1)"></button>
      </div>
    </div>
  </section>

  <section class="cta-section" aria-labelledby="cta-title">
    <div class="cta-gradient" aria-hidden="true"></div>
    <div class="cta-content motion-reveal">
      <p class="eyebrow">Start your shortlist</p>
      <h2 id="cta-title">Ready To Find Your Next Car?</h2>
      <div class="hero-actions">
        <a class="premium-button premium-button-light" routerLink="/table-list">Browse Cars</a>
        <a class="premium-button premium-button-ghost" routerLink="/contact">Contact Us</a>
      </div>
    </div>
  </section>

  <footer class="premium-footer" aria-label="Carmotive CRM footer">
    <div>
      <h2>Carmotive CRM</h2>
      <p>Premium CRM workflows for automotive leads, EV inventory, luxury vehicles, and future mobility.</p>
    </div>
    <nav aria-label="Footer links">
      <a routerLink="/about">About</a>
      <a routerLink="/contact">Contact</a>
      <a routerLink="/table-list">Browse</a>
      <a routerLink="/home">Newsletter</a>
    </nav>
    <form class="newsletter" aria-label="Newsletter signup" (submit)="$event.preventDefault()">
      <label for="newsletter-email">Newsletter</label>
      <div>
        <input id="newsletter-email" type="email" placeholder="Email address" aria-label="Email address" />
        <button type="submit">Join</button>
      </div>
    </form>
    <div class="social-links" aria-label="Social links">
      <a href="https://www.instagram.com/upcomingcarcrm/" target="_blank" rel="noopener">Instagram</a>
      <a href="https://www.youtube.com/@upcomingcarcrm/" target="_blank" rel="noopener">YouTube</a>
      <a href="https://www.linkedin.com/in/piyush-kumar-anand-096959355/" target="_blank" rel="noopener">LinkedIn</a>
    </div>
  </footer>
</section>
